import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateCurrencyInput,
  CurrenciesListResponse,
  Currency,
  UpdateCurrencyInput,
} from '@/features/catalog/types/currency.types';
import { apiFetch } from '@/lib/api';

/**
 * Catalog currencies — TanStack Query hooks.
 *
 * Currencies are GLOBAL reference data (no tenant_id) and
 * reads are open to any authenticated user. Writes require
 * `catalog.manage_currencies` (ADMIN only). The list endpoint
 * accepts `page`, `pageSize`, `sort` (`code` | `name`),
 * `dir`, plus the standard `isActive` / `includeInactive`.
 */

export type CurrenciesSortKey = 'code' | 'name';
export type SortDirection = 'asc' | 'desc';

export interface CurrenciesListParams {
  page: number;
  pageSize: number;
  sort?: CurrenciesSortKey;
  dir?: SortDirection;
  isActive?: 'active' | 'inactive' | 'all';
}

function buildQuery(params: CurrenciesListParams): string {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page));
  sp.set('pageSize', String(params.pageSize));
  if (params.sort) sp.set('sort', params.sort);
  if (params.dir) sp.set('dir', params.dir);
  if (params.isActive === 'inactive') sp.set('isActive', 'false');
  if (params.isActive === 'all') sp.set('includeInactive', 'true');
  return sp.toString();
}

export const currenciesKeys = {
  all: ['currencies'] as const,
  list: (params: CurrenciesListParams) => ['currencies', 'list', params] as const,
  detail: (id: string) => ['currencies', 'detail', id] as const,
};

export function useCurrencies(params: CurrenciesListParams) {
  return useQuery({
    queryKey: currenciesKeys.list(params),
    queryFn: () => apiFetch<CurrenciesListResponse>(`/currencies?${buildQuery(params)}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useCurrency(id: string | null) {
  return useQuery({
    queryKey: currenciesKeys.detail(id ?? ''),
    queryFn: () => apiFetch<Currency>(`/currencies/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateCurrency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCurrencyInput) =>
      apiFetch<Currency>('/currencies', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: currenciesKeys.all }),
  });
}

export function useUpdateCurrency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCurrencyInput }) =>
      apiFetch<Currency>(`/currencies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: currenciesKeys.all });
      qc.invalidateQueries({ queryKey: currenciesKeys.detail(id) });
    },
  });
}

export function useDeleteCurrency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean; id: string }>(`/currencies/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: currenciesKeys.all }),
  });
}
